import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';

import PrimaryButton from '../ui/buttons/primaryButton';
import EventChangePreview from '../events/eventChangePreview';
import gameFormatService from '../../features/gameFormat/gameFormatService';
import {
  applyReservePromotion,
  previewReservePromotion
} from '../../features/event/eventSlice';
import { setAlertInfo } from '../../features/alert/alertSlice';
import { getGameByEventId } from '../../features/game/gameSlice';
import { getBracketsByEventId } from '../../features/bracket/bracketSlice';

const playerLabel = (player) =>
  [player?.name, player?.surname].filter(Boolean).join(' ').trim() || 'Unknown player';

/**
 * Promotes one reserve into the active lineup. The replaced active player moves
 * back to the reserves list, so the team keeps its spare. Everything goes
 * through the server preview/apply protocol: the review shows every count and
 * skipped record before the preview hash is sent back.
 */
const ReservePromotionDialog = ({ open, onClose, team }) => {
  const dispatch = useDispatch();
  const event = useSelector((state) => state.event.event);

  const [step, setStep] = useState('setup');
  const [reserveId, setReserveId] = useState('');
  const [replacePlayerId, setReplacePlayerId] = useState('');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const [appliedResult, setAppliedResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const currentTeam = useMemo(
    () => (event?.teams || []).find((candidate) => String(candidate.id) === String(team?.id)),
    [event?.teams, team?.id]
  );
  const reserves = Array.isArray(currentTeam?.reserves) ? currentTeam.reserves : [];
  const members = Array.isArray(currentTeam?.members) ? currentTeam.members : [];
  const teamLimit = event?.gameFormat
    ? gameFormatService.getGameTypeLimit(event.gameFormat.gameType) / 2
    : 0;
  const lineupFull = teamLimit > 0 && members.length >= teamLimit;

  const reset = () => {
    setStep('setup');
    setReserveId('');
    setReplacePlayerId('');
    setIncludeCompleted(false);
    setPreview(null);
    setLastPayload(null);
    setAppliedResult(null);
    setBusy(false);
    setErrorMessage('');
  };

  useEffect(() => {
    if (!open) return;
    reset();
    // Reset only when the dialog opens or a different team is used.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, team?.id]);

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const buildPayload = () => ({
    teamId: currentTeam?.id,
    reservePlayerId: reserveId,
    ...(replacePlayerId ? { replacePlayerId } : {}),
    includeCompleted
  });

  const canPreview =
    Boolean(currentTeam?.id) &&
    Boolean(reserveId) &&
    (!lineupFull || Boolean(replacePlayerId));

  const runPreview = async (payload) => {
    setBusy(true);
    setErrorMessage('');
    try {
      const result = await dispatch(previewReservePromotion({ eventId: event.id, payload })).unwrap();
      if (result.alreadyApplied) {
        reset();
        onClose?.();
        dispatch(setAlertInfo('That reserve promotion has already been applied to this event.'));
        return;
      }
      setPreview(result);
      setLastPayload(payload);
      setStep('review');
    } catch (error) {
      setErrorMessage(error?.message || 'The preview could not be generated.');
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = () => {
    if (!canPreview) return;
    runPreview(buildPayload());
  };

  const handleApply = async () => {
    if (!preview?.previewHash || !lastPayload) return;
    setBusy(true);
    setErrorMessage('');
    try {
      const result = await dispatch(
        applyReservePromotion({
          eventId: event.id,
          payload: { ...lastPayload, previewHash: preview.previewHash }
        })
      ).unwrap();

      if (result.alreadyApplied) {
        reset();
        onClose?.();
        dispatch(setAlertInfo('That reserve promotion has already been applied to this event.'));
        return;
      }

      // Keep the dialog open on the applied summary so the organiser can see
      // the lineup, bench and every cascaded record that changed.
      setAppliedResult(result);
      setStep('done');
      dispatch(getGameByEventId(event.id));
      dispatch(getBracketsByEventId(event.id));
    } catch (error) {
      if (error?.code === 'EVENT_CHANGED') {
        try {
          const fresh = await dispatch(
            previewReservePromotion({ eventId: event.id, payload: lastPayload })
          ).unwrap();
          setPreview(fresh);
          dispatch(
            setAlertInfo('The event changed since the preview. The preview has been refreshed.')
          );
        } catch (previewError) {
          setErrorMessage(previewError?.message || 'The preview could not be refreshed.');
        }
      } else if (error?.code === 'NO_CHANGES' || error?.alreadyApplied) {
        reset();
        onClose?.();
        dispatch(setAlertInfo(error.message || 'There is nothing left to change.'));
      } else {
        const partial = error?.details?.partialFailure
          ? ' Some records were already written — preview again before retrying.'
          : '';
        setErrorMessage(`${error?.message || 'The promotion failed.'}${partial}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const isReview = step === 'review';
  const isDone = step === 'done';
  let caption = currentTeam?.name || 'Move a reserve into the active lineup.';
  if (isReview) caption = 'Review the changes before applying.';
  if (isDone) caption = 'Summary of what changed.';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6" color="primary">
          {isDone ? 'Reserve promoted' : 'Promote Reserve'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {errorMessage ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        ) : null}

        {isDone ? (
          <EventChangePreview
            result={{
              ...preview,
              counts: appliedResult?.counts || preview?.counts,
              warnings: appliedResult?.warnings || [],
              errors: [],
              alreadyApplied: false
            }}
            kind="reserve"
            applied
          />
        ) : !isReview ? (
          <Stack gap={2}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Current lineup
              </Typography>
              <Typography variant="body2">
                {members.length > 0
                  ? members.map((member) => playerLabel(member)).join(', ')
                  : 'No active players yet.'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {lineupFull
                  ? 'The lineup is full. Choose the active player to move to the reserves list.'
                  : 'The lineup has a free slot. You can promote without moving anyone.'}
              </Typography>
            </Box>

            <TextField
              select
              fullWidth
              label="Reserve to promote"
              value={reserveId}
              onChange={(changeEvent) => setReserveId(changeEvent.target.value)}
              helperText="Reserves do not play until promoted."
              disabled={busy}>
              {reserves.length === 0 ? <MenuItem value="">No reserves</MenuItem> : null}
              {reserves.map((reserve) => (
                <MenuItem key={reserve.id} value={reserve.id}>
                  {playerLabel(reserve)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="Active player moved to reserves"
              value={replacePlayerId}
              onChange={(changeEvent) => setReplacePlayerId(changeEvent.target.value)}
              helperText={
                lineupFull
                  ? 'Required because the lineup is full.'
                  : 'Optional. Leave empty to use the free lineup slot.'
              }
              disabled={busy || members.length === 0}>
              {!lineupFull ? <MenuItem value="">Join the lineup (no player moved)</MenuItem> : null}
              {members.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {playerLabel(member)}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={includeCompleted}
                  onChange={(changeEvent) => setIncludeCompleted(changeEvent.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Include completed records
                  <Typography variant="caption" color="text.secondary" display="block">
                    Also update completed games, finalised section records and completed bracket
                    matches.
                  </Typography>
                </Typography>
              }
            />
          </Stack>
        ) : (
          <EventChangePreview result={preview} kind="reserve" />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {isDone ? (
          <Button onClick={handleClose}>Close</Button>
        ) : !isReview ? (
          <>
            <Button onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <PrimaryButton
              variant="contained"
              onClick={handlePreview}
              disabled={!canPreview || busy}>
              {busy ? <CircularProgress size={20} /> : 'Preview changes'}
            </PrimaryButton>
          </>
        ) : (
          <>
            <Button onClick={() => setStep('setup')} disabled={busy}>
              Back
            </Button>
            <PrimaryButton
              variant="contained"
              onClick={handleApply}
              disabled={busy || !preview?.previewHash || Boolean(preview?.blocked)}>
              {busy ? <CircularProgress size={20} /> : 'Apply promotion'}
            </PrimaryButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReservePromotionDialog;
